import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  AppBar,
  Toolbar,
  Card,
  CardContent,
} from '@mui/material';
import {
  Build as BuildIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';

const About = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
      title: 'Transparent Approvals',
      description: 'Three-tier approval workflow ensures everyone is on the same page throughout the project lifecycle.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48 }} />,
      title: 'Secure & Reliable',
      description: 'Built with enterprise-grade security to protect your project data and sensitive information.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      title: 'Streamlined Process',
      description: 'Eliminate paperwork and reduce delays with our efficient digital approval system.',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 48 }} />,
      title: 'Collaboration First',
      description: 'Connect contractors, homeowners, and admins in one unified platform for seamless communication.',
    },
  ];

  return (
    <>
      {/* Top Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 2 }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/logo.png"
              alt="Congrow Logo"
              style={{ height: '60px', marginRight: '16px', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
          </Box>

          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button color="primary" onClick={() => navigate('/about')}>
              About Us
            </Button>
            <Button color="primary" onClick={() => navigate('/contact')}>
              Contact
            </Button>
            <Button
              color="primary"
              startIcon={<BuildIcon />}
              onClick={() => navigate('/login?role=contractor')}
            >
              Contractor
            </Button>
            <Button
              color="primary"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/login?role=homeowner')}
            >
              Homeowner
            </Button>
            <Button
              color="primary"
              startIcon={<AdminIcon />}
              onClick={() => navigate('/login?role=admin')}
            >
              Admin
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          minHeight: '100vh',
          background: '#003d5c',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px, 100px 100px, 10px 10px, 10px 10px',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Paper elevation={10} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              textAlign="center"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              About Congrow
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" paragraph>
              Building Trust Through Transparency
            </Typography>

            {/* Mission Statement */}
            <Box sx={{ mt: 6, mb: 6 }}>
              <Typography variant="h4" gutterBottom fontWeight={600} color="primary">
                Our Mission
              </Typography>
              <Typography variant="body1" paragraph fontSize={18} color="text.secondary">
                At Congrow, we believe that successful construction projects are built on trust,
                transparency, and clear communication. Our platform connects contractors, homeowners,
                and project administrators in a unified workspace where everyone can track progress,
                approve work, and manage budgets with complete confidence.
              </Typography>
              <Typography variant="body1" paragraph fontSize={18} color="text.secondary">
                We're revolutionizing the construction industry by eliminating paperwork, reducing
                miscommunication, and providing real-time visibility into every aspect of your project.
                From initial planning to final approval, Congrow ensures peace of mind at every step.
              </Typography>
            </Box>

            {/* Features Grid */}
            <Box sx={{ mt: 6, mb: 6 }}>
              <Typography variant="h4" gutterBottom fontWeight={600} color="primary" textAlign="center" mb={4}>
                Why Choose Congrow?
              </Typography>
              <Grid container spacing={4}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderTop: '4px solid #1976d2',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                        <Box sx={{ color: 'primary.main', mb: 2 }}>
                          {feature.icon}
                        </Box>
                        <Typography variant="h5" gutterBottom fontWeight={600}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Our Story */}
            <Box sx={{ mt: 6, mb: 4 }}>
              <Typography variant="h4" gutterBottom fontWeight={600} color="primary">
                Our Story
              </Typography>
              <Typography variant="body1" paragraph fontSize={18} color="text.secondary">
                Founded by construction industry veterans who experienced firsthand the challenges of
                project management, Congrow was created to solve the real-world problems that plague
                construction projects: miscommunication, budget overruns, and approval delays.
              </Typography>
              <Typography variant="body1" paragraph fontSize={18} color="text.secondary">
                Our team combines decades of construction experience with cutting-edge technology to
                deliver a platform that truly understands the needs of contractors, homeowners, and
                project administrators. We're not just building software – we're building better
                construction experiences.
              </Typography>
            </Box>

            {/* Call to Action */}
            <Box textAlign="center" mt={6}>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Ready to Get Started?
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary" mb={3}>
                Join thousands of contractors and homeowners who trust Congrow for their projects.
              </Typography>
              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <Button variant="contained" size="large" onClick={() => navigate('/register')}>
                  Sign Up Now
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/contact')}>
                  Contact Us
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/')}>
                  Back to Home
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default About;
